import React, { useMemo, useCallback, useRef, useLayoutEffect, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Prediction } from '../types/predictions';
import { formatPrediction } from '../utils/priceUtils';
import { formatDate } from '../utils/dateUtils';
import { darkTheme, media } from '../theme';
import { useScrollContext } from '../context/ScrollContext';
import InfluencerScoreDots from './mobile/InfluencerScoreDots';
import SharePrediction from './SharePrediction';

type FilterType = 'all' | 'pending' | 'success' | 'miss' | 'updated' | 'bet' | 'sentiment';

interface ContestEntry {
  price_prediction: number;
  submitted_at: string;
  contest_name?: string;
}

interface PredictionPanelProps {
  predictions: Prediction[];
  activePredictions: number[];
  togglePrediction: (id: number) => void;
  activeFilter: FilterType;
  applyFilter: (filter: FilterType) => void;
  hasPremiumAccess?: boolean;
  // Contest entry props (optional)
  userContestEntry?: ContestEntry | null;
  userHandle?: string;
  entryCount?: number;
}

// Styled components
const Panel = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  height: calc(100vh - 300px);
  min-height: 600px;
  max-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  box-sizing: border-box;
  border: 1px solid ${props => props.theme.border};

  // Make all text in prediction panel white in dark mode
  color: ${props => props.theme.text};
  
  // Ensure all numbers and text inside are visible in dark mode
  span, p, h1, h2, h3, h4, h5, h6, div {
    color: ${props => props.theme.text} !important;
  }

  ${media.mobile} {
    height: 300px;
    border-radius: 12px;
  }
`;

const PanelHeader = styled.div`
  padding: 16px;
  padding-bottom: 12px; 
  border-bottom: 1px solid ${props => props.theme.border};
  flex-shrink: 0;
  h3 {
    margin: 0;
    color: ${props => props.theme.text};
    margin-bottom: 8px;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const FilterButtonGroup = styled.div`
  display: flex;
  gap: 4px;
`;

const FilterButton = styled.button<{ $active: boolean }>`
  background: ${props => props.$active ? props.theme.activeBackground : props.theme.surface};
  color: ${props => props.$active ? props.theme.primary : props.theme.subText};
  border: 1px solid ${props => props.$active ? props.theme.primary : props.theme.border};
  border-radius: 8px;
  padding: 2px 6px;
  font-size: 10px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.$active ? props.theme.hoverActiveBackground : props.theme.hoverBackground};
  }
`;

const InfluencerFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfluencerDropdown = styled.select`
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 6px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  max-width: 200px;
  flex: 1;
  
  &:hover, &:focus {
    border-color: ${props => props.theme.primary};
    outline: none;
  }
  
  option {
    background: ${props => props.theme.surface};
    color: ${props => props.theme.text};
  }
`;

// Searchable dropdown components
const SearchableDropdownContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 200px;
`;

const SearchInput = styled.input`
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 6px 8px;
  font-size: 12px;
  cursor: text;
  transition: all 0.2s;
  width: 100%;
  box-sizing: border-box;
  
  &:hover, &:focus {
    border-color: ${props => props.theme.primary};
    outline: none;
  }
  
  &::placeholder {
    color: ${props => props.theme.text};
    opacity: 0.5;
  }
`;

const DropdownList = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'block' : 'none'};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 100;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
`;

const DropdownItem = styled.div<{ $isSelected?: boolean }>`
  padding: 8px 12px;
  font-size: 12px;
  cursor: pointer;
  background: ${props => props.$isSelected ? props.theme.primary + '20' : 'transparent'};
  color: ${props => props.theme.text};
  
  &:hover {
    background: ${props => props.theme.primary + '30'};
  }
  
  &:first-child {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const AddInfluencerButton = styled.button<{ $active?: boolean }>`
  background: ${props => props.theme.surface};
  color: ${props => props.$active ? '#ff6b35' : props.theme.primary};
  border: 1px solid ${props => props.$active ? '#ff6b35' : props.theme.border};
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 24px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${props => props.$active ? '#ff6b35' + '20' : props.theme.primary + '20'};
    border-color: ${props => props.$active ? '#ff6b35' : props.theme.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ResetButton = styled.button`
  background: ${props => props.theme.surface};
  color: #dc3545;
  border: 1px solid #dc3545;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 10px;
  cursor: pointer;
  transition: all 0.2s;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #dc354520;
  }
`;

// Navigation components for overview mode
const NavigationControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px;
  background: ${props => props.theme.surface};
  border-top: 1px solid ${props => props.theme.border};
  margin-top: auto;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  min-height: 50px;
  flex-shrink: 0;
`;

const NavButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background: ${props => props.disabled ? props.theme.background : props.theme.surface};
  color: ${props => props.disabled ? props.theme.subText : props.theme.text};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 70px;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.primary};
    color: white;
    border-color: ${props => props.theme.primary};
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0,0,0,0.15);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
`;

const NavigationInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 80px;
`;

const NavigationText = styled.span`
  font-size: 12px;
  color: ${props => props.theme.text};
  text-align: center;
  font-weight: 600;
`;

const KeyboardHints = styled.div`
  font-size: 10px;
  color: ${props => props.theme.subText};
  text-align: center;
`;

const KeyHint = styled.kbd`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 3px;
  padding: 2px 4px;
  font-size: 9px;
  margin: 0 1px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
`;

const PredictionList = styled.div`
  padding: 16px;
  padding-bottom: 0;
  overflow-y: auto;
  flex-grow: 1;
  box-sizing: border-box;
  height: calc(100% - 49px);
  
  /* Keep only basic scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.primary}80;
  }
`;

const UpgradeCard = styled.a`
  display: block;
  margin: 8px 0 16px 0;
  padding: 16px;
  border: 1px dashed ${props => props.theme.primary};
  border-radius: 12px;
  text-decoration: none;
  background: ${props => props.theme.activeBackground};
`;

const UpgradeTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.theme.text};
  margin-bottom: 6px;
`;

const UpgradeText = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: ${props => props.theme.subText};
`;

const shouldForwardProp = (prop: string): boolean => !['$active', '$status'].includes(prop);

const PredictionButton = styled.div.withConfig({
  shouldForwardProp,
})<{ $active: boolean }>`
  padding: 12px;
  margin: 0 0 8px 0;
  width: 100%;
  text-align: left;
  background: ${props => props.$active ? props.theme.activeBackground : props.theme.surface};
  border: 1px solid ${props => props.$active ? props.theme.activeBorder : props.theme.border};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.theme.text};
  
  /* Status-based styling */
  &.prediction-status-success {
    background: ${props => props.$active ? 'rgba(46, 204, 113, 0.15)' : 'rgba(46, 204, 113, 0.08)'};
    border-color: ${props => props.$active ? 'rgba(46, 204, 113, 0.6)' : 'rgba(46, 204, 113, 0.3)'};
    
    .status-indicator {
      color: rgba(39, 174, 96, 1);
      font-weight: bold;
    }
  }
  
  &.prediction-status-miss {
    background: ${props => props.$active ? 'rgba(231, 76, 60, 0.15)' : 'rgba(231, 76, 60, 0.08)'};
    border-color: ${props => props.$active ? 'rgba(231, 76, 60, 0.6)' : 'rgba(231, 76, 60, 0.3)'};
    
    .status-indicator {
      color: rgba(192, 57, 43, 1);
      font-weight: bold;
    }
  }
  
  &.prediction-status-updated {
    background: ${props => props.$active ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)'};
    border-color: ${props => props.$active ? 'rgba(156, 39, 176, 0.8)' : 'rgba(156, 39, 176, 0.4)'};
    
    .status-indicator {
      color: rgba(142, 36, 170, 1);
      font-weight: bold;
    }
  }

  &.prediction-status-pending {
    background: ${props => props.$active ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.08)'};
    border-color: ${props => props.$active ? 'rgba(255, 152, 0, 0.6)' : 'rgba(255, 152, 0, 0.3)'};
    
    .status-indicator {
      color: rgba(255, 152, 0, 1);
      font-weight: bold;
    }
  }
  
  &:hover {
    border-color: ${props => props.theme.hoverBorder};
    background: ${props => props.$active ? props.theme.hoverActiveBackground : props.theme.hoverBackground};
    
    &.prediction-status-success:hover {
      background: rgba(46, 204, 113, 0.2);
      border-color: rgba(46, 204, 113, 0.7);
    }
    
    &.prediction-status-miss:hover {
      background: rgba(231, 76, 60, 0.2);
      border-color: rgba(231, 76, 60, 0.7);
    }
    
    &.prediction-status-pending:hover {
      background: rgba(255, 152, 0, 0.18);
      border-color: rgba(255, 152, 0, 0.6);
    }
  }

  strong {
    display: block;
    color: ${props => props.theme.text};
    margin-bottom: 4px;
  }

  div {
    color: ${props => props.theme.text} !important;
  }
  
  small {
    color: ${props => props.theme.text};
  }

  .prediction-indicator {
    display: inline-block;
    width: 10px;
    height: 10px;
    margin-right: 5px;
    border-radius: 50%;
  }
  
  .prediction-type-range {
    border-radius: 0;
    background-color: ${props => props.theme.primary}80;
  }
  
  .prediction-type-target {
    border-radius: 50%;
    background-color: ${props => props.theme.primary};
  }
  
  .prediction-type-pricetimerange {
    border-radius: 0;
    background-color: ${props => props.theme.primary};
    height: 2px;
    margin-top: 4px;
  }
  
  .prediction-type-top {
    border-radius: 0;
    background-color: #ff6b35;
    height: 2px;
    margin-top: 2px;
  }
  
  .prediction-type-bottom {
    border-radius: 0;
    background-color: #4CAF50;
    height: 2px;
    margin-bottom: 2px;
  }
`;



const PredictionTypeBadge = styled.span<{ $type: string }>`
  display: inline-block;
  font-size: ${props => props.$type === 'bet' ? '16px' : '8px'};
  font-weight: bold;
  padding: 1px 4px;
  margin-left: ${props => props.$type === 'bet' ? '-20px' : '0px'};
  margin-right: 8px;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => props.$type === 'top' && `
    background-color: #ff6b35;
    color: white;
  `}
  
  ${props => props.$type === 'bottom' && `
    background-color: #4CAF50;
    color: white;
  `}
  
  ${props => props.$type === 'bet' && `
    background-color: transparent;
    color: #F7931A;
    margin-right: 4px;
  `}
`;

// Countdown Timer Styled Components (Subtle/Compact for Desktop)
const CountdownBanner = styled.div`
  background: linear-gradient(90deg, rgba(247, 147, 26, 0.08) 0%, rgba(247, 147, 26, 0.03) 100%);
  border-bottom: 1px solid rgba(247, 147, 26, 0.2);
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  
  &:hover {
    background: linear-gradient(90deg, rgba(247, 147, 26, 0.12) 0%, rgba(247, 147, 26, 0.06) 100%);
  }
`;

const CountdownLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CountdownIcon = styled.span`
  font-size: 14px;
`;

const CountdownInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const CountdownLabel = styled.span`
  font-size: 9px;
  color: ${props => props.theme.subText};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  opacity: 0.8;
`;

const CountdownInfluencer = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.text};
`;

const CountdownRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CountdownTime = styled.div`
  display: flex;
  align-items: baseline;
  gap: 2px;
  font-variant-numeric: tabular-nums;
`;

const CountdownValue = styled.span`
  font-size: 14px;
  font-weight: bold;
  color: ${props => props.theme.primary || '#F7931A'};
`;

const CountdownUnit = styled.span`
  font-size: 9px;
  color: ${props => props.theme.subText};
  margin-right: 4px;
`;

const CountdownDate = styled.span`
  font-size: 10px;
  color: ${props => props.theme.subText};
  opacity: 0.7;
`;

// Contest Entry Card Styles
const ContestEntryCard = styled.div`
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.15), rgba(76, 175, 80, 0.05));
  border: 2px solid #4caf50;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #4caf50, #8bc34a, #4caf50);
    animation: shimmer 2s infinite;
  }
  
  @keyframes shimmer {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }
`;

const ContestEntryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const ContestBadge = styled.span`
  background: #4caf50;
  color: #fff;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
`;

const ContestHandle = styled.span`
  color: #4caf50;
  font-size: 11px;
  font-weight: 600;
`;

const ContestPrice = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #4caf50;
  text-align: center;
  margin: 8px 0;
  text-shadow: 0 0 10px rgba(76, 175, 80, 0.3);
`;

const ContestMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 10px;
  color: ${props => props.theme.subText};
  margin-top: 8px;
`;

const EntryNumber = styled.span`
  background: rgba(76, 175, 80, 0.2);
  padding: 2px 6px;
  border-radius: 8px;
  color: #4caf50;
`;

const StablePredictionPanel: React.FC<PredictionPanelProps> = ({
  predictions,
  activePredictions,
  togglePrediction,
  activeFilter,
  applyFilter,
  hasPremiumAccess = false,
  userContestEntry,
  userHandle,
  entryCount
}) => {
  const FREE_PREDICTION_LIMIT = 25;
  const location = useLocation();
  const listRef = useRef<HTMLDivElement>(null);
  const { saveScrollPosition, restoreScrollPosition } = useScrollContext();
  const SCROLL_KEY = 'prediction-panel';
  const [assetPredictions, setAssetPredictions] = useState<Prediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // State for influencer filtering
  const [selectedInfluencer, setSelectedInfluencer] = useState<string>('all');
  const [multipleInfluencerMode, setMultipleInfluencerMode] = useState<boolean>(false);
  const [selectedInfluencers, setSelectedInfluencers] = useState<Set<string>>(new Set());
  const [countdown, setCountdown] = useState<{ days: number; hours: number; minutes: number; seconds: number } | null>(null);
  // Searchable dropdown state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Find the next upcoming prediction (pending with soonest end_date)
  // Use predictions prop directly to ensure countdown shows immediately
  const nextPrediction = useMemo(() => {
    const now = new Date();
    const pendingPredictions = predictions.filter(p => {
      const isPending = !p.success_status || p.success_status === 'pending';
      const targetDate = p.end_date ? new Date(p.end_date) : (p.target_time ? new Date(p.target_time) : null);
      return isPending && targetDate && targetDate > now;
    });
    
    if (pendingPredictions.length === 0) return null;
    
    pendingPredictions.sort((a, b) => {
      const aDate = new Date(a.end_date || a.target_time || 0);
      const bDate = new Date(b.end_date || b.target_time || 0);
      return aDate.getTime() - bDate.getTime();
    });
    
    return pendingPredictions[0];
  }, [predictions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (!nextPrediction) {
      setCountdown(null);
      return;
    }

    const targetDate = new Date(nextPrediction.end_date || nextPrediction.target_time || 0);
    
    const updateCountdown = () => {
      const now = new Date();
      const diff = targetDate.getTime() - now.getTime();
      
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, [nextPrediction]);

  // Format price for countdown display
  const getCountdownPriceDisplay = useCallback((pred: Prediction) => {
    if (pred.price_prediction) {
      const price = typeof pred.price_prediction === 'string' 
        ? pred.price_prediction.replace(/[^0-9]/g, '')
        : pred.price_prediction;
      return `$${Number(price).toLocaleString()}`;
    }
    if (pred.price_min && pred.price_max) {
      return `$${Number(pred.price_min).toLocaleString()} - $${Number(pred.price_max).toLocaleString()}`;
    }
    if (pred.prediction_type === 'top') return 'Market Top';
    if (pred.prediction_type === 'bottom') return 'Market Bottom';
    return 'Target';
  }, []);

  // Determine current asset from route
  const currentAsset = useMemo(() => {
    if (location.pathname.includes('/mstr')) return 'mstr';
    if (location.pathname.includes('/naka')) return 'naka';
    return 'btc'; // default
  }, [location.pathname]);

  // Asset-specific panel titles
  const panelTitles = {
    btc: 'Bitcoin Predictions',
    mstr: 'Strategy₿ Predictions',
    naka: 'Nakamoto Predictions'
  };

useEffect(() => {
  setAssetPredictions(predictions);
  // Reset influencer filters when asset changes
  setSelectedInfluencer('all');
  setMultipleInfluencerMode(false);
  setSelectedInfluencers(new Set());
}, [predictions, currentAsset]);

  // 🎯 Auto-scroll to active prediction card when it changes (e.g., deep link)
  useEffect(() => {
    if (activePredictions.length === 1 && listRef.current) {
      // Small delay to let DOM update with new sorting
      setTimeout(() => {
        const activeId = activePredictions[0];
        const activeCard = listRef.current?.querySelector(`[data-prediction-id="${activeId}"]`);
        if (activeCard) {
          activeCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [activePredictions]);

  // Keep scroll position stable - no automatic restoration

  // Enhanced click handlers with stable positioning
  const handlePredictionClick = useCallback((predId: number) => {
    // Don't save scroll position - we want to maintain stable list order
    togglePrediction(predId);
  }, [togglePrediction]);

  const handleFilterClick = useCallback((filter: FilterType) => {
    // Don't save scroll position for filter changes either
    applyFilter(filter);
  }, [applyFilter]);

  // Handle influencer filter change
  const handleInfluencerChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const influencer = event.target.value;
    
    // Save current scroll position before state change
    if (listRef.current) {
      saveScrollPosition(SCROLL_KEY, listRef.current.scrollTop);
    }
    
    setSelectedInfluencer(influencer);
    
    if (!multipleInfluencerMode) {
      // SINGLE INFLUENCER MODE: Deactivate all predictions first, then activate only the selected influencer's
      if (influencer !== 'all') {
        // First, deactivate ALL currently active predictions
        activePredictions.forEach(predId => {
          togglePrediction(predId);
        });
        
        // Then activate all predictions for the selected influencer
        const influencerPredictions = assetPredictions.filter(pred => 
          pred.influencer?.name === influencer
        );
        
        influencerPredictions.forEach(pred => {
          togglePrediction(pred.id);
        });
      }
    } else {
      // MULTIPLE INFLUENCER MODE: Add to the set of selected influencers
      const newSelectedInfluencers = new Set(selectedInfluencers);
      if (influencer !== 'all' && !newSelectedInfluencers.has(influencer)) {
        newSelectedInfluencers.add(influencer);
        setSelectedInfluencers(newSelectedInfluencers);
        
        // Activate predictions for the newly added influencer
        const influencerPredictions = assetPredictions.filter(pred => 
          pred.influencer?.name === influencer
        );
        
        influencerPredictions.forEach(pred => {
          if (!activePredictions.includes(pred.id)) {
            togglePrediction(pred.id);
          }
        });
      }
    }
  }, [saveScrollPosition, currentAsset, assetPredictions, activePredictions, togglePrediction, multipleInfluencerMode, selectedInfluencers]);

  // Handle adding influencer in multiple mode
  const handleAddInfluencer = useCallback(() => {
    if (selectedInfluencer !== 'all' && !selectedInfluencers.has(selectedInfluencer)) {
      const newSelectedInfluencers = new Set(selectedInfluencers);
      newSelectedInfluencers.add(selectedInfluencer);
      setSelectedInfluencers(newSelectedInfluencers);
      
      // Activate predictions for the added influencer
      const influencerPredictions = assetPredictions.filter(pred => 
        pred.influencer?.name === selectedInfluencer
      );
      
      influencerPredictions.forEach(pred => {
        if (!activePredictions.includes(pred.id)) {
          togglePrediction(pred.id);
        }
      });
    }
  }, [selectedInfluencer, selectedInfluencers, assetPredictions, activePredictions, togglePrediction]);

  // Toggle multiple influencer mode
  const toggleMultipleInfluencerMode = useCallback(() => {
    setMultipleInfluencerMode(!multipleInfluencerMode);
    if (!multipleInfluencerMode) {
      // Entering multiple mode - add current influencer to set if not 'all'
      if (selectedInfluencer !== 'all') {
        setSelectedInfluencers(new Set([selectedInfluencer]));
      }
    } else {
      // Exiting multiple mode - clear the set
      setSelectedInfluencers(new Set());
    }
  }, [multipleInfluencerMode, selectedInfluencer]);

  // Reset influencer selection and all filters
  const resetInfluencerSelection = useCallback(() => {
    // Deactivate all active predictions
    activePredictions.forEach(predId => {
      togglePrediction(predId);
    });
    
    // Reset to default state
    setSelectedInfluencers(new Set());
    setSelectedInfluencer('all');
    setMultipleInfluencerMode(false);
  }, [activePredictions, togglePrediction]);

  // Get unique influencers for dropdown
  const uniqueInfluencers = useMemo(() => {
    const influencers = new Set<string>();
    assetPredictions.forEach(pred => {
      if (pred.influencer?.name) {
        influencers.add(pred.influencer.name);
      }
    });
    return Array.from(influencers).sort();
  }, [assetPredictions]);

  // Asset info for submit card
  const assetInfo = useMemo(() => {
    switch (currentAsset) {
      case 'btc': return { name: 'Bitcoin', symbol: 'BTC', icon: '₿' };
      case 'mstr': return { name: 'MicroStrategy', symbol: 'MSTR', icon: '📈' };
      case 'naka': return { name: 'Nakamoto Games', symbol: 'NAKA', icon: '🎮' };
      default: return { name: 'Crypto', symbol: 'CRYPTO', icon: '💰' };
    }
  }, [currentAsset]);

  const limitPredictions = useCallback((list: Prediction[]) => {
    return hasPremiumAccess ? list : list.slice(0, FREE_PREDICTION_LIMIT);
  }, [hasPremiumAccess]);



  // Create prediction buttons for current asset
  const predictionButtons = useMemo(() => {
    if (isLoading) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          Loading {currentAsset.toUpperCase()} predictions...
        </div>
      );
    }

    if (assetPredictions.length === 0) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          <div style={{ marginBottom: '10px' }}>
            No {currentAsset.toUpperCase()} predictions yet
          </div>
          <div style={{ fontSize: '14px' }}>
            Be the first to submit a {currentAsset.toUpperCase()} prediction!
          </div>
        </div>
      );
    }

    const sortedPredictions = [...assetPredictions]
      .filter(pred => {
        // FIRST: Filter by the current filter type (show ALL predictions that match the filter, not just active ones)
        if (activeFilter !== 'all') {
          if (activeFilter === 'pending') {
            if (pred.success_status && pred.success_status !== 'pending') return false;
          } else if (activeFilter === 'bet') {
            if (pred.prediction_type !== 'bet') return false;
          } else if (activeFilter === 'sentiment') {
            if (pred.prediction_type !== 'sentiment') return false;
          } else if (activeFilter === 'updated') {
            if (pred.success_status !== 'updated') return false;
          } else {
            // For 'success' or 'miss' filters
            if (pred.success_status !== activeFilter) return false;
          }
        }
        
        // SECOND: Filter by influencer based on mode
        if (multipleInfluencerMode && selectedInfluencers.size > 0) {
          // Multiple influencer mode: show predictions from any selected influencer
          return pred.influencer?.name && selectedInfluencers.has(pred.influencer.name);
        } else if (selectedInfluencer !== 'all') {
          // Single influencer mode: show predictions from selected influencer
          return pred.influencer?.name === selectedInfluencer;
        }
        return true;
      })
      .sort((a, b) => {
        // 🎯 FIRST: Active predictions go to the TOP
        const aIsActive = activePredictions.includes(a.id);
        const bIsActive = activePredictions.includes(b.id);
        if (aIsActive && !bIsActive) return -1;
        if (!aIsActive && bIsActive) return 1;
        
        // Sort by nearest upcoming target date (next to resolve first)
        const aTargetDate = a.end_date ? new Date(a.end_date) : (a.target_time ? new Date(a.target_time) : null);
        const bTargetDate = b.end_date ? new Date(b.end_date) : (b.target_time ? new Date(b.target_time) : null);
        
        // Determine if predictions are pending (not yet resolved)
        const aIsPending = !a.success_status || a.success_status === 'pending';
        const bIsPending = !b.success_status || b.success_status === 'pending';
        
        // Pending predictions come before resolved ones
        if (aIsPending && !bIsPending) return -1;
        if (!aIsPending && bIsPending) return 1;
        
        // For pending predictions: sort by nearest upcoming date
        if (aIsPending && bIsPending) {
          if (!aTargetDate && !bTargetDate) return 0;
          if (!aTargetDate) return 1; // No date goes to end
          if (!bTargetDate) return -1;
          return aTargetDate.getTime() - bTargetDate.getTime();
        }
        
        // For resolved predictions: most recently resolved first
        if (!aTargetDate && !bTargetDate) return 0;
        if (!aTargetDate) return 1;
        if (!bTargetDate) return -1;
        return bTargetDate.getTime() - aTargetDate.getTime();
      });

    const visiblePredictions = limitPredictions(sortedPredictions);
    const hiddenCount = sortedPredictions.length - visiblePredictions.length;

    return (
      <>
        {visiblePredictions.map((pred) => {
      const isActive = activePredictions.includes(pred.id);
      

      return (
        <PredictionButton 
          key={`${currentAsset}-${pred.id}`}
          $active={isActive}
          onClick={() => handlePredictionClick(pred.id)}
          className={`prediction-item prediction-item-${pred.id} prediction-status-${pred.success_status || 'pending'}`}
          data-prediction-id={pred.id}
        >
          <strong>
            {pred.prediction_type !== 'sentiment' && (
              <span className={`prediction-indicator prediction-type-${pred.prediction_type || 'target'}`}></span>
            )}
            {pred.prediction_type === 'sentiment' && (
              <span style={{ 
                marginRight: '6px', 
                fontSize: '14px',
                color: '#64B5F6' 
              }}>
                💬
              </span>
            )}
            {(pred.prediction_type === 'top' || pred.prediction_type === 'bottom' || pred.prediction_type === 'bet') && (
              <PredictionTypeBadge $type={pred.prediction_type}>
                {pred.prediction_type === 'bet' ? '🎲' : pred.prediction_type}
              </PredictionTypeBadge>
            )}
            {pred.influencer?.name || 'Unknown'}
            {pred.prediction_type !== 'sentiment' && (
              <span className="status-indicator" style={{ 
                marginLeft: '8px',
                fontSize: '11px', 
                fontWeight: 'bold',
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: pred.success_status === 'success' ? 'rgba(39, 174, 96, 0.15)' : 
                               pred.success_status === 'miss' ? 'rgba(192, 57, 43, 0.15)' : 
                               pred.success_status === 'updated' ? 'rgba(139, 92, 246, 0.15)' :
                               'rgba(255, 152, 0, 0.15)',
                color: pred.success_status === 'success' ? 'rgba(39, 174, 96, 1)' : 
                       pred.success_status === 'miss' ? 'rgba(192, 57, 43, 1)' : 
                       pred.success_status === 'updated' ? 'rgba(139, 92, 246, 1)' :
                     'rgba(255, 152, 0, 1)'
              }}>
                {pred.success_status === 'success' ? 'SUCCESS' : 
                 pred.success_status === 'miss' ? 'MISS' : 
                 pred.success_status === 'updated' ? 'UPDATED' :
                 'PENDING'}
              </span>
            )}
          </strong>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: '#666', marginTop: '4px' }}>
            <span>Track Record:</span>
            <InfluencerScoreDots 
              influencerName={pred.influencer?.name || 'Unknown'} 
              allPredictions={assetPredictions}
            />
          </div>
          {formatPrediction(pred)}
          <br />
          <small>
            {pred.prediction_type === 'sentiment' ? (
              // For sentiment tweets, only show when it was made
              pred.prediction_made && (
                <>📅 Made: {formatDate(pred.prediction_made, 'dd/MM/yyyy')}</>
              )
            ) : (
              // For price predictions, show target date and made date
              <>
                {pred.prediction_type === 'target' ? '🎯 Target: ' : 
                pred.prediction_type === 'top' ? '🔝 Top: ' :
                pred.prediction_type === 'bottom' ? '📉 Bottom: ' :
                pred.prediction_type === 'pricetimerange' ? '⏱️ Price by: ' :
                pred.prediction_type === 'bet' ? 
                  // For bets, check if finished (success or miss) vs still pending
                  (pred.success_status === 'success' || pred.success_status === 'miss') 
                    ? '🎲 Finished: ' 
                    : '🎲 Due date: ' :
                '📊 Range: '}
                {pred.target_time ? formatDate(pred.target_time, 'dd/MM/yyyy') : pred.end_date ? formatDate(pred.end_date, 'dd/MM/yyyy') : 'Date not specified'}
                {pred.prediction_made && (
                  <>
                    <br />
                    📅 Made: {formatDate(pred.prediction_made, 'dd/MM/yyyy')}
                  </>
                )}
              </>
            )}
          </small>
          <div 
            style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}
            onClick={(e) => e.stopPropagation()}
          >
            <SharePrediction prediction={pred} subtle />
          </div>
        </PredictionButton>
      );
    })}
        {!hasPremiumAccess && hiddenCount > 0 && (
          <UpgradeCard href="/pricing">
            <UpgradeTitle>Unlock full prediction history</UpgradeTitle>
            <UpgradeText>
              Showing the first {visiblePredictions.length} predictions. Upgrade for deeper history and long-term ad-free access.
            </UpgradeText>
          </UpgradeCard>
        )}
      </>
    );
  }, [assetPredictions, activePredictions, handlePredictionClick, currentAsset, isLoading, selectedInfluencer, activeFilter, multipleInfluencerMode, selectedInfluencers, limitPredictions, hasPremiumAccess]);

  // Stable filter buttons
  const filterButtons = useMemo(() => {
    return (
      <FilterButtonGroup>
        <FilterButton 
          $active={activeFilter === 'pending'} 
          onClick={() => handleFilterClick('pending')}
        >
          🔄 Pend
        </FilterButton>
        <FilterButton 
          $active={activeFilter === 'success'} 
          onClick={() => handleFilterClick('success')}
        >
          ✅ Win
        </FilterButton>
        <FilterButton 
          $active={activeFilter === 'updated'} 
          onClick={() => handleFilterClick('updated')}
        >
          🔄 Updated
        </FilterButton>
        <FilterButton 
          $active={activeFilter === 'miss'} 
          onClick={() => handleFilterClick('miss')}
        >
          ❌ Miss
        </FilterButton>
        <FilterButton 
          $active={activeFilter === 'bet'} 
          onClick={() => handleFilterClick('bet')}
        >
          🎲 Bet
        </FilterButton>
        <FilterButton 
          $active={activeFilter === 'sentiment'} 
          onClick={() => handleFilterClick('sentiment')}
        >
          💬 Talk
        </FilterButton>
      </FilterButtonGroup>
    );
  }, [activeFilter, handleFilterClick]);

  // Asset names for the panel titles (keeping for backward compatibility)
  const assetTitleInfo = {
    btc: { name: 'Bitcoin', symbol: 'BTC' },
    mstr: { name: 'MicroStrategy', symbol: 'MSTR' },
    naka: { name: 'Nakamoto', symbol: 'NAKA' }
  };

  // Navigation logic for overview mode
  const currentActiveIndex = useMemo(() => {
    if (activePredictions.length === 0 || assetPredictions.length === 0) return -1;
    
    // Get the filtered predictions that are actually displayed
    const displayedPredictions = limitPredictions([...assetPredictions]
      .filter(pred => {
        // Apply same filtering logic as the displayed list
        if (activeFilter !== 'all') {
          if (activeFilter === 'pending') {
            if (pred.success_status && pred.success_status !== 'pending') return false;
          } else if (activeFilter === 'bet') {
            if (pred.prediction_type !== 'bet') return false;
          } else if (activeFilter === 'sentiment') {
            if (pred.prediction_type !== 'sentiment') return false;
          } else if (activeFilter === 'updated') {
            if (pred.success_status !== 'updated') return false;
          } else {
            // For 'success' or 'miss' filters
            if (pred.success_status !== activeFilter) return false;
          }
        }
        
        // Apply influencer filtering
        if (multipleInfluencerMode && selectedInfluencers.size > 0) {
          return pred.influencer?.name && selectedInfluencers.has(pred.influencer.name);
        } else if (selectedInfluencer !== 'all') {
          return pred.influencer?.name === selectedInfluencer;
        }
        return true;
      })
      .sort((a, b) => {
        const aDate = new Date(a.prediction_made || a.created_at || 0).getTime();
        const bDate = new Date(b.prediction_made || b.created_at || 0).getTime();
        return bDate - aDate;
      }));
    
    return displayedPredictions.findIndex(pred => activePredictions.includes(pred.id));
  }, [activePredictions, assetPredictions, activeFilter, multipleInfluencerMode, selectedInfluencers, selectedInfluencer, limitPredictions]);

  // Removed auto-scroll to prevent jarring UX when activating predictions

  const handleNavigatePrevious = useCallback(() => {
    // Get the filtered predictions that are actually displayed
    const displayedPredictions = limitPredictions([...assetPredictions]
      .filter(pred => {
        // Apply same filtering logic as the displayed list
        if (activeFilter !== 'all') {
          if (activeFilter === 'pending') {
            if (pred.success_status && pred.success_status !== 'pending') return false;
          } else if (activeFilter === 'bet') {
            if (pred.prediction_type !== 'bet') return false;
          } else if (activeFilter === 'sentiment') {
            if (pred.prediction_type !== 'sentiment') return false;
          } else if (activeFilter === 'updated') {
            if (pred.success_status !== 'updated') return false;
          } else {
            // For 'success' or 'miss' filters
            if (pred.success_status !== activeFilter) return false;
          }
        }
        
        // Apply influencer filtering
        if (multipleInfluencerMode && selectedInfluencers.size > 0) {
          return pred.influencer?.name && selectedInfluencers.has(pred.influencer.name);
        } else if (selectedInfluencer !== 'all') {
          return pred.influencer?.name === selectedInfluencer;
        }
        return true;
      })
      .sort((a, b) => {
        const aDate = new Date(a.prediction_made || a.created_at || 0).getTime();
        const bDate = new Date(b.prediction_made || b.created_at || 0).getTime();
        return bDate - aDate;
      }));
    
    
    if (displayedPredictions.length === 0) return;
    
    const currentDisplayedIndex = displayedPredictions.findIndex(pred => activePredictions.includes(pred.id));
    let targetIndex = currentDisplayedIndex - 1;
    if (targetIndex < 0) targetIndex = displayedPredictions.length - 1; // Wrap to end
    
    // Deactivate current prediction if any
    if (currentDisplayedIndex >= 0) {
      const currentId = displayedPredictions[currentDisplayedIndex].id;
      if (activePredictions.includes(currentId)) {
        togglePrediction(currentId);
      }
    }
    
    // Activate previous prediction
    const targetId = displayedPredictions[targetIndex].id;
    if (!activePredictions.includes(targetId)) {
      togglePrediction(targetId);
    }
  }, [assetPredictions, activePredictions, togglePrediction, activeFilter, multipleInfluencerMode, selectedInfluencers, selectedInfluencer, limitPredictions]);

  const handleNavigateNext = useCallback(() => {
    // Get the filtered predictions that are actually displayed
    const displayedPredictions = limitPredictions([...assetPredictions]
      .filter(pred => {
        // Apply same filtering logic as the displayed list
        if (activeFilter !== 'all') {
          if (activeFilter === 'pending') {
            if (pred.success_status && pred.success_status !== 'pending') return false;
          } else if (activeFilter === 'bet') {
            if (pred.prediction_type !== 'bet') return false;
          } else if (activeFilter === 'sentiment') {
            if (pred.prediction_type !== 'sentiment') return false;
          } else if (activeFilter === 'updated') {
            if (pred.success_status !== 'updated') return false;
          } else {
            // For 'success' or 'miss' filters
            if (pred.success_status !== activeFilter) return false;
          }
        }
        
        // Apply influencer filtering
        if (multipleInfluencerMode && selectedInfluencers.size > 0) {
          return pred.influencer?.name && selectedInfluencers.has(pred.influencer.name);
        } else if (selectedInfluencer !== 'all') {
          return pred.influencer?.name === selectedInfluencer;
        }
        return true;
      })
      .sort((a, b) => {
        const aDate = new Date(a.prediction_made || a.created_at || 0).getTime();
        const bDate = new Date(b.prediction_made || b.created_at || 0).getTime();
        return bDate - aDate;
      }));
    
    if (displayedPredictions.length === 0) return;
    
    const currentDisplayedIndex = displayedPredictions.findIndex(pred => activePredictions.includes(pred.id));
    let targetIndex = currentDisplayedIndex + 1;
    if (targetIndex >= displayedPredictions.length) targetIndex = 0; // Wrap to beginning
    
    // Deactivate current prediction if any
    if (currentDisplayedIndex >= 0) {
      const currentId = displayedPredictions[currentDisplayedIndex].id;
      if (activePredictions.includes(currentId)) {
        togglePrediction(currentId);
      }
    }
    
    // Activate next prediction
    const targetId = displayedPredictions[targetIndex].id;
    if (!activePredictions.includes(targetId)) {
      togglePrediction(targetId);
    }
  }, [assetPredictions, activePredictions, togglePrediction, activeFilter, multipleInfluencerMode, selectedInfluencers, selectedInfluencer, limitPredictions]);

  // Debug countdown (commented out - was flooding console)
  // console.log('🕐 Countdown debug:', { 
  //   predictionsCount: predictions.length,
  //   nextPrediction: nextPrediction ? { id: nextPrediction.id, name: nextPrediction.influencer?.name, end_date: nextPrediction.end_date } : null,
  //   countdown 
  // });

  return (
    <Panel>
      {/* Countdown Timer Banner */}
      {nextPrediction && countdown && (
        <CountdownBanner onClick={() => togglePrediction(nextPrediction.id)}>
          <CountdownLeft>
            <CountdownIcon>⏰</CountdownIcon>
            <CountdownInfo>
              <CountdownLabel>Next prediction due</CountdownLabel>
              <CountdownInfluencer>
                {nextPrediction.influencer?.name}: {getCountdownPriceDisplay(nextPrediction)}
              </CountdownInfluencer>
            </CountdownInfo>
          </CountdownLeft>
          <CountdownRight>
            <CountdownTime>
              {countdown.days > 0 && (
                <>
                  <CountdownValue>{countdown.days}</CountdownValue>
                  <CountdownUnit>d</CountdownUnit>
                </>
              )}
              <CountdownValue>{countdown.hours.toString().padStart(2, '0')}</CountdownValue>
              <CountdownUnit>h</CountdownUnit>
              <CountdownValue>{countdown.minutes.toString().padStart(2, '0')}</CountdownValue>
              <CountdownUnit>m</CountdownUnit>
              <CountdownValue>{countdown.seconds.toString().padStart(2, '0')}</CountdownValue>
              <CountdownUnit>s</CountdownUnit>
            </CountdownTime>
          </CountdownRight>
        </CountdownBanner>
      )}
      <PanelHeader>
        <h3>{panelTitles[currentAsset] || 'Expert Predictions'}</h3>
        <FilterContainer>
          <InfluencerFilterContainer>
            <SearchableDropdownContainer ref={dropdownRef}>
              <SearchInput
                type="text"
                placeholder={selectedInfluencer === 'all' ? 'All Influencers' : selectedInfluencer}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setIsDropdownOpen(true);
                }}
                onFocus={() => setIsDropdownOpen(true)}
              />
              <DropdownList $isOpen={isDropdownOpen}>
                <DropdownItem
                  $isSelected={selectedInfluencer === 'all'}
                  onClick={() => {
                    setSelectedInfluencer('all');
                    setSearchQuery('');
                    setIsDropdownOpen(false);
                    handleInfluencerChange({ target: { value: 'all' } } as any);
                  }}
                >
                  All Influencers
                </DropdownItem>
                {uniqueInfluencers
                  .filter(inf => inf.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map(influencer => (
                    <DropdownItem
                      key={influencer}
                      $isSelected={selectedInfluencer === influencer}
                      onClick={() => {
                        setSelectedInfluencer(influencer);
                        setSearchQuery('');
                        setIsDropdownOpen(false);
                        handleInfluencerChange({ target: { value: influencer } } as any);
                      }}
                    >
                      {influencer}
                    </DropdownItem>
                  ))}
              </DropdownList>
            </SearchableDropdownContainer>
            <AddInfluencerButton
              $active={multipleInfluencerMode}
              onClick={toggleMultipleInfluencerMode}
              title={multipleInfluencerMode ? "Switch to single influencer mode" : "Switch to multiple influencer mode"}
            >
              ➕
            </AddInfluencerButton>
            <ResetButton
              onClick={resetInfluencerSelection}
              title="Reset all filters and predictions"
            >
              ↻
            </ResetButton>
          </InfluencerFilterContainer>
          <FilterButtonGroup>
            <FilterButton 
              $active={activeFilter === 'pending'} 
              onClick={() => handleFilterClick('pending')}
            >
              🔄 Pend
            </FilterButton>
            <FilterButton 
              $active={activeFilter === 'success'} 
              onClick={() => handleFilterClick('success')}
            >
              ✅ Win
            </FilterButton>
            <FilterButton 
              $active={activeFilter === 'miss'} 
              onClick={() => handleFilterClick('miss')}
            >
              ❌ Miss
            </FilterButton>
            
            <FilterButton 
              $active={activeFilter === 'bet'} 
              onClick={() => handleFilterClick('bet')}
            >
              🎲 Bet
            </FilterButton>
            
            <FilterButton 
              $active={activeFilter === 'sentiment'} 
              onClick={() => handleFilterClick('sentiment')}
            >
              💬 Talk
            </FilterButton>
          </FilterButtonGroup>
        </FilterContainer>
      </PanelHeader>
      <PredictionList 
        ref={listRef}
        data-testid="prediction-list"
      >
        {/* Contest Entry Card - shown at top when user has entered */}
        {userContestEntry && (
          <ContestEntryCard>
            <ContestEntryHeader>
              <ContestBadge>🎯 Your Entry</ContestBadge>
              {userHandle && <ContestHandle>@{userHandle}</ContestHandle>}
            </ContestEntryHeader>
            <ContestPrice>${userContestEntry.price_prediction.toLocaleString()}</ContestPrice>
            <ContestMeta>
              <span>📅 {new Date(userContestEntry.submitted_at).toLocaleDateString()}</span>
              {entryCount && <EntryNumber>#{entryCount} entry</EntryNumber>}
            </ContestMeta>
            <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '10px', opacity: 0.7 }}>
              April 28 • Bitcoin Conference 2026
            </div>
          </ContestEntryCard>
        )}
        {predictionButtons}
      </PredictionList>
      
      {/* Navigation Controls for Overview Mode */}
      <NavigationControls>
        <NavButton
          onClick={handleNavigatePrevious}
          disabled={assetPredictions.length === 0}
          title="Previous prediction (↑ or ←)"
        >
          ↑ Prev
        </NavButton>
        
        <NavigationInfo>
          <NavigationText>
            {(() => {
              // Calculate displayed predictions count
              const displayedCount = limitPredictions([...assetPredictions]
                .filter(pred => {
                  // Apply same filtering logic as the displayed list
                  if (activeFilter !== 'all') {
                    if (activeFilter === 'pending') {
                      if (pred.success_status && pred.success_status !== 'pending') return false;
                    } else if (activeFilter === 'bet') {
                      if (pred.prediction_type !== 'bet') return false;
                    } else if (activeFilter === 'sentiment') {
                      if (pred.prediction_type !== 'sentiment') return false;
                    } else if (activeFilter === 'updated') {
                      if (pred.success_status !== 'updated') return false;
                    } else {
                      // For 'success' or 'miss' filters
                      if (pred.success_status !== activeFilter) return false;
                    }
                  }
                  
                  // Apply influencer filtering
                  if (multipleInfluencerMode && selectedInfluencers.size > 0) {
                    return pred.influencer?.name && selectedInfluencers.has(pred.influencer.name);
                  } else if (selectedInfluencer !== 'all') {
                    return pred.influencer?.name === selectedInfluencer;
                  }
                  return true;
                })).length;
              
              return currentActiveIndex >= 0 
                ? `${currentActiveIndex + 1} of ${displayedCount}`
                : `0 of ${displayedCount}`;
            })()}
          </NavigationText>
          <KeyboardHints>
            <KeyHint>↑</KeyHint><KeyHint>↓</KeyHint> or <KeyHint>←</KeyHint><KeyHint>→</KeyHint>
          </KeyboardHints>
        </NavigationInfo>
        
        <NavButton
          onClick={handleNavigateNext}
          disabled={assetPredictions.length === 0}
          title="Next prediction (↓ or →)"
        >
          Next ↓
        </NavButton>
      </NavigationControls>
    </Panel>
  );
};

// Simplified memo comparison
export default React.memo(StablePredictionPanel, (prevProps, nextProps) => {
  return (
    prevProps.predictions === nextProps.predictions &&
    prevProps.activeFilter === nextProps.activeFilter &&
    prevProps.activePredictions === nextProps.activePredictions &&
    prevProps.togglePrediction === nextProps.togglePrediction &&
    prevProps.applyFilter === nextProps.applyFilter &&
    prevProps.userContestEntry === nextProps.userContestEntry &&
    prevProps.userHandle === nextProps.userHandle &&
    prevProps.entryCount === nextProps.entryCount
  );
});
